import { Controller } from "@hotwired/stimulus"
import { Modal, Dropdown } from "bootstrap"
import { launchCelebration } from "../utils/confetti"
import { getStep, stepsFor } from "../tutorial/steps"
import { scrollAnchorIntoView, placeCard, forwardWheel } from "../tutorial/geometry"
import { AnchorSettler, SPOTLIGHT_FADE } from "../tutorial/settle"
import {
  buildTray,
  buildReplayPill,
  buildScrim,
  updateScrim,
  buildBlocker,
  buildRing,
  positionRing,
  buildCard,
  renderCardContent,
  positionCard,
  buildCelebration,
} from "../tutorial/ui"

// Guided tutorial (#2991): the "Get started on Sindi" checklist tray, the
// spotlight modules it launches, and the completion celebration. Mounted from
// shared/_demo_tour.html.erb on the two desktop layouts; the server only
// renders the shell for the eligible new-user cohort.
//
// A running module is carried across Turbo navigations by URL params
// (?tour=<step id>&beat=<n>) — Turbo replaces <body> on every visit, so this
// controller reconnects on each page and re-derives everything from the
// params, the Stimulus values and localStorage. Completion is per-user on
// the server; within-module progress is per-browser in localStorage.
export default class extends Controller {
  static values = {
    persona: String,
    completed: Array,
    celebrated: Boolean,
    routes: Object,
    endpoint: String,
    pro: Boolean,
    // Suppress the idle "Get started" tray on content-heavy screens (the step
    // overlays still run). Set per screen by demo_tour_helper#demo_tour_hide_tray?.
    hideTray: Boolean,
  }

  connect() {
    // Turbo preview snapshots render before the real page; doing anything on
    // them paints chrome twice.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.completedSet = new Set(this.completedValue)
    this.opened = null
    this.settler = null
    this.rings = []

    this.handleKeydown = this.handleKeydown.bind(this)
    this.teardownForCache = this.teardownForCache.bind(this)
    window.addEventListener("keydown", this.handleKeydown)
    // Cached snapshots must not carry the overlay — a Back visit would
    // restore a ghost tour with no controller state behind it.
    document.addEventListener("turbo:before-cache", this.teardownForCache)

    const params = new URLSearchParams(window.location.search)
    const stepId = params.get("tour")

    if (stepId && this.stepForPersona(stepId)) {
      this.resumeModule(stepId, parseInt(params.get("beat"), 10) || 0)
    } else if (this.allDone() && !this.celebratedValue) {
      this.showCelebration()
    } else if (!this.allDone()) {
      this.renderTray()
    } else {
      // Track finished and celebrated: the checklist gives way to a
      // collapsed "Replay tutorial" bar.
      this.renderReplayPill()
    }
  }

  disconnect() {
    window.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("turbo:before-cache", this.teardownForCache)
    this.teardownModule()
    this.removeTray()
  }

  // ------------------------------------------------------------------- tray

  // `expand` opens the tray on arrival. Closing the gift modal asks for it
  // (#3018); every other path keeps the stored state.
  renderTray({ expand = false } = {}) {
    this.removeTray()
    // Screens where the tray would cover real content skip it entirely
    // (collapsed and expanded alike); the step overlays are unaffected.
    if (this.hideTrayValue) return

    // The welcome/upgrade modals show for exactly this cohort — the tray
    // waits its turn rather than docking under their backdrop.
    // The gift modal (#3018) counts from first paint rather than from .show,
    // since it is still fading in when this controller connects.
    const blocking = document.querySelector(
      "#onboardingModal.show, #modal_upgrade_account.show, #modal_gifted_trial[data-gifted-trial-open]"
    )
    if (blocking) {
      // The tray docks the moment the modal starts to close, sliding up as
      // the modal fades. Closing the gift modal, whichever way, docks it
      // expanded: the modal's only message is the tour. The welcome and
      // upgrade modals leave the tray in its stored state.
      blocking.addEventListener("hide.bs.modal", () => {
        const expand = blocking.id === "modal_gifted_trial"
        delete blocking.dataset.giftedTrialOpen
        this.renderTray({ expand })
        if (expand) this.updateStorage({ collapsed: false })
      }, { once: true })
      // Focus is left where Bootstrap returns it. Moving it to the tray's
      // header drew a focus ring on the tray the moment it arrived.
      return
    }

    const steps = stepsFor(this.personaValue)
    this.tray = buildTray(steps, {
      completed: this.completedSet,
      progress: this.storage().progress || {},
      collapsed: expand ? false : !!this.storage().collapsed,
    })
    this.element.appendChild(this.tray)
  }

  removeTray() {
    if (this.tray) {
      this.tray.remove()
      this.tray = null
    }
    if (this.replayPill) {
      this.replayPill.remove()
      this.replayPill = null
    }
  }

  // ----------------------------------------------------------- replay pill

  renderReplayPill() {
    this.removeTray()
    // Unlike the checklist tray, the replay bar lives only on the Home
    // screen — a finished user's other screens stay chrome-free.
    if (this.pathOf(this.routesValue.home) !== window.location.pathname) return
    if (this.hideTrayValue) return
    if (this.replayDismissedThisSession()) return

    this.replayPill = buildReplayPill()
    this.element.appendChild(this.replayPill)
  }

  // Resets completion server-side and brings the full checklist back, in
  // place — no navigation needed, the tray renders on every screen.
  replayTutorial() {
    this.postState({ reset: true }).then(() => {
      this.completedSet = new Set()
      this.celebratedValue = false
      this.updateStorage({ progress: {} })
      this.renderTray()
    })
  }

  dismissReplayPill() {
    try {
      window.sessionStorage.setItem(this.replayDismissKey(), "true")
    } catch (error) {
      // Storage unavailable — the pill just returns on the next page.
    }
    this.removeTray()
  }

  replayDismissedThisSession() {
    try {
      return window.sessionStorage.getItem(this.replayDismissKey()) === "true"
    } catch (error) {
      return false
    }
  }

  replayDismissKey() {
    return `${this.storageKey()}:replayDismissed`
  }

  toggleTray() {
    const collapsed = !this.tray.classList.contains("demo-tour-tray--collapsed")
    this.tray.classList.toggle("demo-tour-tray--collapsed", collapsed)
    const header = this.tray.querySelector(".demo-tour-tray__header")
    header.setAttribute("aria-expanded", String(!collapsed))
    const chevron = this.tray.querySelector(".demo-tour-tray__chevron")
    chevron.classList.toggle("fa-chevron-up", collapsed)
    chevron.classList.toggle("fa-chevron-down", !collapsed)
    this.updateStorage({ collapsed })
  }

  startStep(event) {
    this.launch(event.currentTarget.dataset.stepId, false)
  }

  restartStep(event) {
    event.stopPropagation()
    this.launch(event.currentTarget.dataset.stepId, true)
  }

  // ------------------------------------------------------------ module flow

  launch(stepId, restart) {
    const beats = this.moduleBeats(stepId)
    if (!beats.length) return

    let at = restart ? 0 : Math.min((this.storage().progress || {})[stepId] || 0, beats.length - 1)
    if (restart) this.clearProgress(stepId)

    this.navigateWithTour(this.beatUrl(stepId, beats, at))
  }

  resumeModule(stepId, at) {
    this.activeStepId = stepId
    this.beats = this.moduleBeats(stepId)
    if (!this.beats.length) return this.exitModule()

    this.beatIndex = Math.min(Math.max(at, 0), this.beats.length - 1)
    this.removeTray()
    this.enterBeat(this.beatIndex)
  }

  nextBeat() {
    if (this.activeStepId == null) return
    if (this.beatIndex >= this.beats.length - 1) return this.completeModule()
    this.moveToBeat(this.beatIndex + 1)
  }

  prevBeat() {
    if (this.activeStepId == null || this.beatIndex === 0) return
    this.moveToBeat(this.beatIndex - 1)
  }

  moveToBeat(index) {
    const target = this.beats[index]
    const url = this.beatUrl(this.activeStepId, this.beats, index)
    if (this.pathOf(url) !== window.location.pathname) {
      this.saveProgress(index)
      this.navigateWithTour(url)
      return
    }

    this.beatIndex = index
    window.history.replaceState(window.history.state, "", url)
    this.enterBeat(index)
  }

  enterBeat(index) {
    const beat = this.beats[index]
    this.saveProgress(index)
    this.closeOpened()
    this.disposeSettler()

    this.ensureOverlay(beat)

    if (beat.open) this.openBeatUI(beat.open)
    if (beat.anchor) {
      window.requestAnimationFrame(() => scrollAnchorIntoView(beat.anchor))

      this.settler = new AnchorSettler(beat.anchor, {
        square: !!beat.square,
        previousRect: this.currentRect || null,
        onCommit: (rect, outgoing) => this.paintBeat(beat, index, rect, outgoing),
        onTrack: (rect) => this.trackBeat(beat, rect),
        onTimeout: () => this.skipBeat(index),
      })
    } else {
      this.paintBeat(beat, index, null, this.currentRect || null)
    }
  }

  // The safety net: a beat whose anchor never appears (deleted sample,
  // hidden pro UI, future drift) steps aside instead of stranding the tour.
  skipBeat(index) {
    if (index >= this.beats.length - 1) {
      this.completeModule()
    } else {
      this.moveToBeat(index + 1)
    }
  }

  completeModule() {
    const stepId = this.activeStepId
    this.completedSet.add(stepId)
    this.clearProgress(stepId)
    this.teardownModule()

    this.postState({ completed_step: stepId }).finally(() => {
      this.navigateWithTour(this.routesValue.home)
    })
  }

  exitModule() {
    this.teardownModule()
    const url = new URL(window.location.href)
    url.searchParams.delete("tour")
    url.searchParams.delete("beat")
    window.history.replaceState(window.history.state, "", url.toString())
    if (!this.allDone()) {
      this.renderTray()
    } else if (this.celebratedValue) {
      this.renderReplayPill()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.activeStepId != null) this.exitModule()
  }

  // --------------------------------------------------------- overlay layers

  ensureOverlay(beat) {
    if (!this.blocker) {
      this.blocker = buildBlocker()
      this.element.appendChild(this.blocker)
    }
    // Blocking clicks and passing scroll cannot come from the same property,
    // so the wheel is forwarded by hand on the beats that keep scrolling live.
    if (this.wheelHandler) {
      this.blocker.removeEventListener("wheel", this.wheelHandler)
      this.wheelHandler = null
    }
    if (beat.scroll) {
      this.wheelHandler = (e) => forwardWheel(e, this.blocker)
      this.blocker.addEventListener("wheel", this.wheelHandler)
    }

    if (!this.scrim) {
      this.scrim = buildScrim()
      this.element.appendChild(this.scrim.svg)
    }
    this.scrim.svg.style.display = beat.dim === false ? "none" : ""

    if (!this.card) {
      this.card = buildCard()
      // Rendered hidden until the first anchor commits — appearing at
      // viewport centre and sliding over reads as the card arriving twice.
      this.card.card.style.visibility = "hidden"
      this.element.appendChild(this.card.card)
    }
  }

  paintBeat(beat, index, rect, outgoing) {
    this.currentRect = rect

    updateScrim(this.scrim.path, beat.dim === false ? null : rect)

    // Geometry is never transitioned — one box morphing into the next reads
    // as the bracket sliding around the screen. Each beat gets its own ring
    // that fades in while the last one fades out.
    this.clearRings()
    if (outgoing) {
      const fadingRing = buildRing(outgoing, true)
      this.element.appendChild(fadingRing)
      this.rings.push(fadingRing)
      window.setTimeout(() => {
        fadingRing.remove()
        this.rings = this.rings.filter((r) => r !== fadingRing)
      }, SPOTLIGHT_FADE)
    }
    if (rect) {
      this.ring = buildRing(rect)
      this.element.appendChild(this.ring)
      this.rings.push(this.ring)
    }

    renderCardContent(this.card.content, beat, index, this.beats.length)
    positionCard(this.card.card, placeCard(rect, beat.place, beat.beside, beat.pin))
    this.card.card.style.visibility = ""
  }

  trackBeat(beat, rect) {
    this.currentRect = rect
    updateScrim(this.scrim.path, beat.dim === false ? null : rect)
    if (this.ring) positionRing(this.ring, rect)
    positionCard(this.card.card, placeCard(rect, beat.place, beat.beside, beat.pin))
  }

  clearRings() {
    this.rings.forEach((ring) => ring.remove())
    this.rings = []
    this.ring = null
  }

  teardownModule() {
    this.disposeSettler()
    this.closeOpened()
    this.clearRings()
    if (this.wheelHandler && this.blocker) {
      this.blocker.removeEventListener("wheel", this.wheelHandler)
      this.wheelHandler = null
    }
    if (this.blocker) { this.blocker.remove(); this.blocker = null }
    if (this.scrim) { this.scrim.svg.remove(); this.scrim = null }
    if (this.card) {
      if (this.card.card._resizeObserver) this.card.card._resizeObserver.disconnect()
      this.card.card.remove()
      this.card = null
    }
    this.currentRect = null
    this.activeStepId = null
  }

  teardownForCache() {
    this.teardownModule()
    this.removeTray()
    if (this.celebration) { this.celebration.remove(); this.celebration = null }
  }

  disposeSettler() {
    if (this.settler) {
      this.settler.destroy()
      this.settler = null
    }
  }

  // ------------------------------------------- beat-owned modals & dropdowns

  openBeatUI(open) {
    this.fadeStrippedEl = null
    try {
      if (open.modal) {
        const el = document.querySelector(open.modal)
        if (el) {
          // Snap the modal in instead of fading: a running fade animation keeps
          // the anchor "still animating", so the ring lingers on the previous
          // beat until the fade finishes. Restored on close.
          if (el.classList.contains("fade")) {
            el.classList.remove("fade")
            this.fadeStrippedEl = el
          }
          // Some modals load their body on demand when a remote trigger is
          // clicked (e.g. the new-conversation form); showing the bare shell
          // leaves them empty. When the beat names a trigger, click it so the
          // app fills and opens the modal itself.
          const trigger = open.trigger ? document.querySelector(open.trigger) : null
          if (trigger) {
            trigger.click()
          } else {
            // focus: false — Bootstrap's focustrap would steal the keyboard
            // from the tour card sitting above the modal.
            Modal.getOrCreateInstance(el, { focus: false }).show()
          }
        }
      } else if (open.dropdown) {
        const toggle = document.querySelector(open.dropdown)
        // autoClose: false — a click on the blocker bubbles to document and
        // would close the menu the beat is about.
        if (toggle) Dropdown.getOrCreateInstance(toggle, { autoClose: false }).show()
      }
      this.opened = open
    } catch (error) {
      this.opened = null
    }
  }

  closeOpened() {
    if (!this.opened) return
    try {
      if (this.opened.modal) {
        const el = document.querySelector(this.opened.modal)
        // getOrCreateInstance, not getInstance: a trigger-opened modal may
        // still be mid-load when the beat is left, so ensure we can hide it.
        if (el) Modal.getOrCreateInstance(el, { focus: false }).hide()
        // Restore the fade we stripped on open, for normal (non-tour) use.
        if (this.fadeStrippedEl) {
          this.fadeStrippedEl.classList.add("fade")
          this.fadeStrippedEl = null
        }
      } else if (this.opened.dropdown) {
        const toggle = document.querySelector(this.opened.dropdown)
        const dropdown = toggle && Dropdown.getInstance(toggle)
        if (dropdown) dropdown.hide()
      }
    } catch (error) {
      // The element may be gone after a navigation — nothing to close.
    }
    this.opened = null
  }

  // ------------------------------------------------------------ celebration

  showCelebration() {
    this.celebration = buildCelebration()
    this.element.appendChild(this.celebration)
    window.requestAnimationFrame(() => {
      const card = this.celebration && this.celebration.querySelector(".demo-tour-celebration__card")
      const box = card && card.getBoundingClientRect()
      // The overlay sits at z 1075: the radial burst goes just under it so it
      // reads as coming from behind the card, the falling and rising layers
      // pass in front.
      launchCelebration(
        box ? { x: box.x + box.width / 2, y: box.y + box.height / 2 } : undefined,
        { behindZ: 1074, frontZ: 1080 }
      )
    })
  }

  dismissCelebration() {
    if (this.celebration) {
      this.celebration.remove()
      this.celebration = null
    }
    this.celebratedValue = true
    this.postState({ celebrated: true })
    // The celebration hands off to the collapsed replay bar.
    this.renderReplayPill()
  }

  // Backdrop-to-close is celebration-only. The action sits on the overlay,
  // so a click that started inside the card bubbles here with a different
  // target and is ignored.
  celebrationBackdropClick(event) {
    if (event.target === event.currentTarget) this.dismissCelebration()
  }

  // ---------------------------------------------------------------- helpers

  stepForPersona(stepId) {
    return stepsFor(this.personaValue).find((s) => s.id === stepId)
  }

  allDone() {
    return stepsFor(this.personaValue).every((s) => this.completedSet.has(s.id))
  }

  // A module's playable beats: pro-gated beats drop on free plans, and beats
  // whose screen cannot be resolved (no referral to land on) drop too.
  moduleBeats(stepId) {
    const step = getStep(stepId)
    return step.beats.filter((beat) => {
      if (beat.requiresPro && !this.proValue) return false
      if (beat.route && !this.routesValue[beat.route]) return false
      return true
    })
  }

  beatUrl(stepId, beats, index) {
    const step = getStep(stepId)
    const routeKey = beats[index].route || step.route
    const base = this.routesValue[routeKey]
    const url = new URL(base, window.location.origin)
    url.searchParams.set("tour", stepId)
    url.searchParams.set("beat", String(index))
    return url.pathname + url.search
  }

  pathOf(url) {
    return new URL(url, window.location.origin).pathname
  }

  // All tour-driven navigation goes through here. The discard-confirm
  // interceptor in packs/application.js cancels any turbo:before-visit while
  // localStorage.isShowModal is 'true', and confirmation_modal_controller
  // arms that flag just by connecting on /referrals/new — clicks are blocked
  // during a module, so the form cannot be dirty and the flag is safe to
  // drop. It re-arms itself on the next /referrals/new connect.
  navigateWithTour(url) {
    try {
      window.localStorage.setItem("isShowModal", "false")
    } catch (error) {
      // Storage unavailable — the interceptor is inert then anyway.
    }
    Turbo.visit(url)
  }

  postState(fields) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const formData = new FormData()
    Object.entries(fields).forEach(([key, value]) => formData.append(key, String(value)))

    return fetch(this.endpointValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": token, Accept: "application/json" },
      body: formData,
    }).catch(() => {})
  }

  // ------------------------------------------------- per-browser progress

  storageKey() {
    const userId = document.querySelector("#server-data")?.dataset?.userId || "anon"
    return `sindiTour:${userId}`
  }

  storage() {
    try {
      return JSON.parse(window.localStorage.getItem(this.storageKey())) || {}
    } catch (error) {
      return {}
    }
  }

  updateStorage(patch) {
    try {
      window.localStorage.setItem(this.storageKey(), JSON.stringify({ ...this.storage(), ...patch }))
    } catch (error) {
      // Private browsing — progress lives for this page only.
    }
  }

  saveProgress(index) {
    const progress = { ...(this.storage().progress || {}) }
    progress[this.activeStepId] = index
    this.updateStorage({ progress })
  }

  clearProgress(stepId) {
    const progress = { ...(this.storage().progress || {}) }
    delete progress[stepId]
    this.updateStorage({ progress })
  }
}
